import React, { FC, useEffect, useRef, useState } from "react";
// eslint-disable-next-line import/named
import { RouteComponentProps } from "@reach/router";
import { Canceler } from "axios";

import {
    AppBanner,
    AppLoader,
    AttendeeCard,
    AppContainer,
    AppBlankDataHelper,
    AppSectionHeader,
} from "../../components";
import { PublicAPI } from "../../apis";
import { cancelAllPrevRequest, errorToast, itemsPerPage } from "../../utils";
import { User } from "../../models";
import { useDetectInViewport, useTranslation } from "../../hooks";

import "./assets/styles/styles.scss";

export const AttendeePage: FC<RouteComponentProps> = (): JSX.Element => {
    const { t } = useTranslation();

    const cancelTokenSourcesRef = useRef<Canceler[]>([]);

    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<User[]>([]);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);

    const isHasLoadMore = !loading && totalItems > data.length && hasMore;

    const fetchData = (page?: number, replaceData?: User[]) => {
        cancelAllPrevRequest(cancelTokenSourcesRef.current);
        setLoading(true);

        const activePage = page || currentPage;

        PublicAPI.getAllUser<User>(
            activePage,
            {
                itemsPerPage,
            },
            (c) => {
                cancelTokenSourcesRef.current.push(c);
            }
        )
            .then(({ response, errorMessage }) => {
                if (errorMessage) {
                    errorToast(errorMessage);
                } else if (response) {
                    const currentData = replaceData || data;

                    setCurrentPage(activePage + 1);
                    setData([...currentData, ...response.items]);
                    setTotalItems(response.totalItems);

                    if (response.items.length < itemsPerPage) {
                        setHasMore(false);
                    } else {
                        setHasMore(true);
                    }
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchData();
    }, []);

    const lastRef = useDetectInViewport(isHasLoadMore, () => {
        fetchData();
    });

    return (
        <div className="attendee-container">
            <AppBanner />

            <AppContainer className="mt-4">
                <AppSectionHeader
                    title={t("attendee.section:label.title")}
                    subTitle={t("attendee.section:label.description")}
                />

                {!!(!loading && !data.length) && <AppBlankDataHelper />}

                <div className="attendee-container--items">
                    <div className="row">
                        {data.map((item) => (
                            <AttendeeCard
                                key={item.id}
                                item={item}
                                isListView
                            />
                        ))}
                    </div>
                    {isHasLoadMore && <div ref={lastRef} />}
                </div>

                {loading && (
                    <AppLoader
                        containerClassName={
                            currentPage !== 1
                                ? "load-more-loader"
                                : "centerLoader"
                        }
                    />
                )}
            </AppContainer>
        </div>
    );
};
